import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Wand2, X } from 'lucide-react';

interface Props {
  top: number;
  left: number;
  selectedText: string;
  isBusy: boolean;
  onSubmit: (instruction: string) => void;
  onClose: () => void;
}

const POPOVER_WIDTH = 320;

const HighlightEditPopover: React.FC<Props> = ({ top, left, selectedText, isBusy, onSubmit, onClose }) => {
  const [instruction, setInstruction] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submit = () => {
    if (!instruction.trim() || isBusy) return;
    onSubmit(instruction.trim());
  };

  return (
    <div
      className="absolute z-30 w-80 bg-white rounded-xl shadow-xl border border-[#E5E7EB] p-3"
      style={{ top, left, width: POPOVER_WIDTH }}
      // Stop mouseup inside the popover from clearing the selection-driven open state.
      onMouseUp={(e) => e.stopPropagation()}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <p className="text-[11px] text-gray-400 line-clamp-2 flex-1">
          “{selectedText.slice(0, 120)}{selectedText.length > 120 ? '…' : ''}”
        </p>
        <button
          onClick={onClose}
          disabled={isBusy}
          className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <textarea
        ref={inputRef}
        value={instruction}
        onChange={(e) => setInstruction(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit();
          if (e.key === 'Escape' && !isBusy) onClose();
        }}
        placeholder="Describe the change for this part…"
        rows={2}
        className="w-full text-sm border border-[#E5E7EB] rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#07E5D1]/40 resize-none"
      />
      <div className="flex items-center justify-end gap-2 mt-2">
        <button
          onClick={submit}
          disabled={!instruction.trim() || isBusy}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-white bg-[#07E5D1] hover:brightness-95 disabled:opacity-50 rounded-lg px-3 py-1.5 transition"
        >
          {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
          {isBusy ? 'Applying…' : 'Apply edit'}
        </button>
      </div>
    </div>
  );
};

export default HighlightEditPopover;
